import { apiFetch, apiUrl } from "./api";
import type { StoredDocument } from "./document-store";

/**
 * Client for the org-owned document store (`/api/documents/*`).
 * Cookie-authenticated. The client owns ids, so writes are idempotent PUT
 * upserts. `document-store.ts` keeps a synchronous localStorage cache and calls
 * these in the background to mirror writes and hydrate from the server.
 */

/**
 * The server payload for an upsert — `StoredDocument` minus id/timestamps and
 * minus the server-owned fields the upsert must never carry back.
 */
/** The columns the documents table sorts by. */
export type DocumentSortKey = "updated" | "name" | "type" | "status";

export type DocumentPayload = Omit<
  StoredDocument,
  "id" | "createdAt" | "updatedAt" | "remindersPromptedAt"
>;

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(apiUrl(path), {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status})`;
    throw new Error(message);
  }
  return data as T;
}

/** All of the owner's documents from the server. */
export function fetchDocuments(): Promise<StoredDocument[]> {
  return req<StoredDocument[]>("/api/documents", { credentials: "include" });
}

/**
 * One document by id. Lets a picker label an already-linked document without
 * pulling the whole library, since the selection won't generally be in whatever
 * page of search results is on screen.
 */
export function fetchDocument(id: string): Promise<StoredDocument> {
  return req<StoredDocument>(`/api/documents/${id}`, {
    credentials: "include",
  });
}

/** Filters + paging for the documents library (server-side). */
export type DocumentsPageParams = {
  limit: number;
  offset: number;
  q?: string;
  type?: string;
  status?: "all" | "draft" | "final" | "signed";
  sort?: DocumentSortKey;
  dir?: "asc" | "desc";
};

/**
 * One row of the library list.
 *
 * Deliberately not a {@link StoredDocument}: a document's bulk is in `output`
 * (the whole generated document) plus `values`, `risk` and `signatures`, and a
 * list showing names and dates was paying for all of it on every row. The
 * backend leaves those columns alone and sends `hasOutput` in their place;
 * anything needing the content fetches the document by id.
 */
export type DocumentListItem = Pick<
  StoredDocument,
  | "id"
  | "kind"
  | "documentType"
  | "typeName"
  | "name"
  | "status"
  | "signingRequestId"
  | "employeeId"
  | "incidentId"
  | "letterheadId"
  | "createdAt"
  | "updatedAt"
> & {
  /** Whether the document has generated output — i.e. there is a PDF to download. */
  hasOutput: boolean;
  portalSentAt: string | null;
  supersedesId?: string;
};

/** One page of documents plus the overall total for the active filters. */
export type DocumentsPage = { items: DocumentListItem[]; total: number };

/** Distinct document type labels for the library's type filter. */
export function fetchDocumentTypes(): Promise<string[]> {
  return req<string[]>("/api/documents/types", { credentials: "include" });
}

/** A filtered, paginated slice of the owner's documents (`/api/documents/page`). */
export function fetchDocumentsPage(params: DocumentsPageParams): Promise<DocumentsPage> {
  const search = new URLSearchParams();
  search.set("limit", String(params.limit));
  search.set("offset", String(params.offset));
  if (params.q) search.set("q", params.q);
  if (params.type) search.set("type", params.type);
  if (params.status && params.status !== "all") search.set("status", params.status);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  return req<DocumentsPage>(`/api/documents/page?${search.toString()}`, {
    credentials: "include",
  });
}

/** Upserts a document by id. */
export function putDocument(id: string, payload: DocumentPayload): Promise<StoredDocument> {
  return req<StoredDocument>(`/api/documents/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/** Deletes a document by id (idempotent for the client). */
export function deleteDocumentRemote(id: string): Promise<void> {
  return req<void>(`/api/documents/${id}`, { method: "DELETE" });
}

/** One reminder picked in the reminder dialog. */
export type ReminderInput = {
  key: string;
  category: string;
  title: string;
  description?: string | null;
  eventDate: string;
  leadDays: number;
};

/** The reminders endpoint's answer: rows written, and the prompt stamp it set. */
export type ConfirmRemindersResult = {
  confirmed: number;
  /** ISO timestamp — the document won't be asked about reminders again. */
  remindersPromptedAt: string;
};

/**
 * Confirms a set of reminders for a document (the picker that opens once per
 * document, from send / send for signature / signing / "Mark as final"). The
 * document must already exist server-side, so callers flush it first.
 *
 * An empty list is the "skip" answer and is worth sending: it writes no alerts
 * but still stamps the document as asked, so a later trigger stays quiet.
 */
export function confirmDocumentReminders(
  id: string,
  reminders: ReminderInput[],
): Promise<ConfirmRemindersResult> {
  return req<ConfirmRemindersResult>(`/api/documents/${id}/reminders`, {
    method: "POST",
    body: JSON.stringify({ reminders }),
  });
}
